import { useReadQuery } from "@apollo/client";
import { blue } from "@mui/material/colors";
import { Box, GridProps, Stack, Typography, useTheme } from "@mui/material";
import { DashboardTile } from "components/Dashboard/DashboardTile";
import { useCallback, useMemo, useState } from "react";
import { useThemeMode } from "hooks/useThemeMode";
import { useRenderTracker } from "hooks/useRenderTracker";
import { metricsLogger } from "utils/metricsLogger";
import { useWasteSubmissionsOverviewContext } from "./WasteSubmissionsOverviewContext";

// Show top 5 states in the bar chart
const TOP_N_STATES = 5;

// Color palette for top 5 states
const STATE_COLORS = [
  blue[700],
  blue[600],
  blue[500],
  blue[400],
  blue[300],
];

type SubmissionStateTileProps = GridProps & {
  onStateClick?: (state: string) => void;
  selectedState?: string | null;
};

export function SubmissionStateTile({
  onStateClick,
  selectedState,
  ...gridProps
}: SubmissionStateTileProps) {
  return (
    <DashboardTile {...gridProps}>
      <SubmissionStateContent
        onStateClick={onStateClick}
        selectedState={selectedState}
      />
    </DashboardTile>
  );
}

// Export content component for use in unified container
export { SubmissionStateContent };

type BarData = {
  state: string;
  count: number;
  percentage: number;
  color: string;
};

function SubmissionStateContent({
  onStateClick,
  selectedState,
}: Pick<SubmissionStateTileProps, "onStateClick" | "selectedState">) {
  useRenderTracker("SubmissionStateContent", { onStateClick, selectedState }, { logger: metricsLogger, logToConsole: false });
  const theme = useTheme();
  const { themeMode } = useThemeMode();
  const [expanded, setExpanded] = useState(false);

  const { byStateQuery } = useWasteSubmissionsOverviewContext();
  const { data } = useReadQuery(byStateQuery);

  const summary = data?.wasteSubmissionsSummary;

  const allStatesData: BarData[] = useMemo(() => {
    if (!summary?.byState || summary.byState.length === 0) return [];

    // Sort by count descending
    const sorted = [...summary.byState].sort((a, b) => b.count - a.count);

    // Scale bars relative to the max count (top bar = 100%)
    const maxCount = sorted[0]?.count ?? 1;

    return sorted.map((item, index) => ({
      state: item.state,
      count: item.count,
      percentage: maxCount > 0 ? Math.round((item.count / maxCount) * 100) : 0,
      color: STATE_COLORS[index % STATE_COLORS.length],
    }));
  }, [summary]);

  const displayedData = expanded
    ? allStatesData
    : allStatesData.slice(0, TOP_N_STATES);
  const hasMore = allStatesData.length > TOP_N_STATES;

  const handleClick = useCallback(
    (state: string) => {
      if (onStateClick) {
        onStateClick(state);
      }
    },
    [onStateClick]
  );

  const isHighlighting = selectedState !== null;

  return (
    <>
      <DashboardTile.Header
        title="By State"
        infoTooltip={<StateTooltip />}
      />
      <DashboardTile.ContentArea>
        {displayedData.length > 0 ? (
          <Stack spacing={1.5} sx={{ py: 0.5 }}>
            {displayedData.map((bar) => {
              const isSelected = selectedState === bar.state;
              const isMuted = isHighlighting && !isSelected;

              return (
                <Stack
                  key={bar.state}
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  onClick={() => handleClick(bar.state)}
                  sx={{
                    cursor: "pointer",
                    "&:hover": { opacity: 0.8 },
                    opacity: isMuted ? 0.4 : 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      width: 28,
                      flexShrink: 0,
                    }}
                  >
                    {bar.state}
                  </Typography>
                  <Box
                    sx={{
                      flexGrow: 1,
                      backgroundColor:
                        themeMode === "light"
                          ? theme.palette.grey[200]
                          : theme.palette.grey[800],
                      borderRadius: 0.5,
                      height: 28,
                    }}
                  >
                    <Box
                      sx={{
                        width: `${bar.percentage}%`,
                        minWidth: bar.percentage > 0 ? 4 : 0,
                        backgroundColor: isMuted
                          ? theme.palette.grey[400]
                          : bar.color,
                        borderRadius: 0.5,
                        borderTopRightRadius: 0,
                        borderBottomRightRadius: 0,
                        height: "100%",
                      }}
                    />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      color: theme.palette.text.secondary,
                      minWidth: 36,
                      textAlign: "right",
                    }}
                  >
                    {bar.count.toLocaleString()}
                  </Typography>
                </Stack>
              );
            })}
            {hasMore && (
              <Typography
                onClick={() => setExpanded(!expanded)}
                sx={{
                  fontSize: "0.75rem",
                  color: theme.palette.primary.main,
                  cursor: "pointer",
                  textAlign: "center",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                {expanded
                  ? "Show less"
                  : `Show ${allStatesData.length - TOP_N_STATES} more`}
              </Typography>
            )}
          </Stack>
        ) : (
          <Typography color="text.secondary" textAlign="center" py={4}>
            No submissions found
          </Typography>
        )}
      </DashboardTile.ContentArea>
    </>
  );
}

function StateTooltip() {
  return (
    <Typography variant="caption">
      <Typography component="span" variant="caption" fontWeight={800}>
        By State
      </Typography>{" "}
      shows submissions grouped by state. Click a bar to filter.
    </Typography>
  );
}
